import {
  POPOVER_CLOSE,
  POPOVER_CLOSE_DEFERRED,
  POPOVER_OPEN_DEFERRED,
  POPOVER_CANCEL,
  closePopover,
  openPopover,
} from '../actions/popover'

import get from 'lodash/get'

/**
 * The idea is that a user can hover over multiple elements that open a
 * popover. Rather than rapidly closing and reopening popovers we will have
 * a period of time to allow it to stay open or reposition.
 * @type {number}
 */
const popoverReopenTimeout = 100

let openTimer
let openAction
let closeTimer

function stopCloseTimer() {
  if (typeof closeTimer === 'number') {
    clearTimeout(closeTimer)
    closeTimer = undefined
  }
}

function stopOpenTimer() {
  if (typeof openTimer === 'number') {
    clearTimeout(openTimer)
    openTimer = undefined
  }
}

function stopBothTimers() {
  stopCloseTimer()
  stopOpenTimer()
}

const popoverMiddleware = ({ getState, dispatch }) => next => function(action) {
  if (!action) return

  const { type } = action

  const result = next(action)

  if (typeof type !== 'string') return result

  function open() {
    stopBothTimers()
    if (openAction === undefined) {
      // it can be canceled and deleted
      return
    }
    dispatch(openPopover(openAction.popoverType, openAction.popoverProps))
  }

  function close() {
    stopBothTimers()
    dispatch(closePopover())
  }

  if (type === POPOVER_CLOSE) {
    return stopBothTimers()
  }

  const isPopoverOpen = typeof getState().getIn(['popover', 'popoverType']) === 'string'

  if (isPopoverOpen === false && type === POPOVER_CANCEL) {
    openAction = undefined
    return stopBothTimers()
  }

  if (type === POPOVER_OPEN_DEFERRED) {
    // Check if we're trying to open a popover while hovering over an existing one
    const currentPopover = getState().getIn(['popover', 'popoverType'])
    const currentPopoverProps = getState().getIn(['popover', 'popoverProps'])
    
    // If there's an active popover and it's different from what we're trying to open
    if (currentPopover && currentPopoverProps) {
      const targetElement = action.popoverProps.targetRef?.current
      const currentPopoverElement = currentPopoverProps.targetRef?.current
      
      if (targetElement && currentPopoverElement) {
        if (currentPopoverElement.contains(targetElement)) {
          // If hovering inside current popover, cancel any pending changes
          stopOpenTimer()
          stopCloseTimer()
          openAction = undefined
          return
        } else {
          // If moving to a new popover target, close current one first
          stopOpenTimer()
          stopCloseTimer()
          dispatch(closePopover())
          
          // Then set up the new popover with normal delay
          openAction = action
          const timeout = get(openAction, 'popoverProps.timeout')
          openTimer = setTimeout(open, timeout)
          return
        }
      }
    }

    // Normal flow for opening when no current popover exists
    const propsChanged = !openAction || 
                        openAction.popoverType !== action.popoverType || 
                        openAction.popoverProps.targetRef !== action.popoverProps.targetRef ||
                        openAction.popoverProps.position !== action.popoverProps.position

    if (propsChanged) {
      stopOpenTimer()
      openAction = action

      if (isPopoverOpen) {
        dispatch(openPopover(openAction.popoverType, openAction.popoverProps))
        return
      }

      const timeout = get(openAction, 'popoverProps.timeout')
      openTimer = setTimeout(open, timeout)
    }
    return
  }

  if (type === POPOVER_CLOSE_DEFERRED && closeTimer === undefined) {
    // schedule close
    closeTimer = setTimeout(close, popoverReopenTimeout)
  }

  return result
}

export default popoverMiddleware
